import type { Process } from "types/types";
import styled from "styled-components/macro";
import { useSelector } from "store/useSelector";
import {
  useFixedTargetsOverride,
  useOutputsAndTargets,
} from "components/AheadBehind/aheadBehindUtils";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { medGrey, producingColor, untaggedColor } from "constants/colors";
import { getExpandedTileContentHeights } from "./ExpandedTileModules";
import { useSettings } from "contexts/SettingsManager/SettingsManager";

/** Expanded Tile module showing Ahead/Behind (netOutput, target, difference) */
// tslint:disable-next-line: cognitive-complexity
export function TileAheadBehindModule({ process }: { process: Process }) {
  const { isLargeTiles, tileScaleMultiplier } = useSettings();
  const dataDumpForProcessTile = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess[process.id]
  );

  const { currentFixedShiftTarget, totalFixedShiftTarget } =
    useFixedTargetsOverride(null, dataDumpForProcessTile, process.id);

  const {
    netOutput: shiftOutput,
    productionPotentialOutput: shiftCurrentTargetOutput,
  } = useOutputsAndTargets(
    dataDumpForProcessTile,
    process.id,
    currentFixedShiftTarget,
    totalFixedShiftTarget
  );

  const aheadBehindAmount =
    shiftOutput && shiftCurrentTargetOutput
      ? shiftOutput - shiftCurrentTargetOutput
      : undefined;

  const loading = !(shiftOutput || shiftOutput === 0);

  const isAhead = aheadBehindAmount && aheadBehindAmount > 0;
  const isBehind = aheadBehindAmount && aheadBehindAmount < 0;

  const [
    aheadBehindAmountFormatted,
    shiftOutputFormatted,
    shiftCurrentTargetOutputFormatted,
  ] = [aheadBehindAmount, shiftOutput, shiftCurrentTargetOutput].map(num =>
    formatCount(num)
  );

  return (
    <TileAheadBehindModuleStyles
      {...{
        $isLargeTiles: isLargeTiles,
        $tileScaleMultiplier: tileScaleMultiplier ** 0.45,
        $color: isAhead ? producingColor : isBehind ? untaggedColor : medGrey,
        $isLongABText: aheadBehindAmountFormatted.length > 5,
        $isLongActualText: shiftOutputFormatted.length > 3,
        $isLongExpectedText: shiftCurrentTargetOutputFormatted.length > 3,
      }}
    >
      {loading ? (
        <Spinner width={10} />
      ) : (
        <>
          <div className="aheadBehindAmount">
            {shiftCurrentTargetOutput && shiftOutput
              ? (isAhead ? "+" : "") + aheadBehindAmountFormatted
              : "--"}
          </div>
          <div className="actualVsExpected">
            <div className="actual">
              <div className="value">
                {shiftOutput ? shiftOutputFormatted : "--"}
              </div>
              <div className="description">Actual</div>
            </div>
            <div className="expected">
              <div className="value">
                {shiftCurrentTargetOutput
                  ? shiftCurrentTargetOutputFormatted
                  : "--"}
              </div>
              <div className="description">Exp.</div>
            </div>
          </div>
        </>
      )}
    </TileAheadBehindModuleStyles>
  );
}

/** round to 0 decimals & switch to "10K" format if above 9999 */
function formatCount(count?: number | null) {
  const num = Number(count);
  const isAbove10k = num > 9999;
  const isAbove10m = num > 9999999;
  return `${
    isAbove10m
      ? (num / 1000000).toFixed(0) + "M"
      : isAbove10k
        ? (num / 1000).toFixed(0) + "K"
        : num.toFixed(0)
  }`;
}

const TileAheadBehindModuleStyles = styled.div<{
  $isLongABText: boolean;
  $isLongActualText: boolean;
  $isLongExpectedText: boolean;
  $isLargeTiles: boolean;
  $tileScaleMultiplier: number;
  $color: string;
}>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: ${props =>
    getExpandedTileContentHeights().tileAheadBehind[
      props.$isLargeTiles ? "lg" : "sm"
    ]}px;
  text-align: center;
  padding: ${props => 5 * (props.$isLargeTiles ? 2 : 1)}px;
  border-bottom: ${props =>
      /* aheadBehind includes bottom-border inside it */
      3 * (props.$isLargeTiles ? 2 : 1)}px
    solid ${props => props.$color};
  border-radius: 0 0 1px 1px;
  .aheadBehindAmount {
    color: ${props => props.$color};
    font-weight: bold;
    padding-bottom: ${props => 5 * (props.$isLargeTiles ? 4 : 1)}px;
    font-size: ${props =>
      15 *
      (props.$isLargeTiles ? (props.$isLongABText ? 2 : 4) : 1) *
      props.$tileScaleMultiplier}px;
    line-height: ${props => 20 * (props.$isLargeTiles ? 2 : 1)}px;
    display: flex;
    justify-content: center;
  }
  .actualVsExpected {
    flex-grow: 1;
    display: flex;
    .actual,
    .expected {
      width: 50%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .value {
        line-height: ${props =>
          16 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
        font-weight: bold;
      }
      .description {
        font-size: ${props =>
          9 * (props.$isLargeTiles ? 2 : 1) * props.$tileScaleMultiplier}px;
        line-height: ${props => 12 * (props.$isLargeTiles ? 2 : 1)}px;
      }
    }
    .actual .value {
      font-size: ${props =>
        12 *
        (props.$isLargeTiles ? (props.$isLongActualText ? 3 : 4) : 1) *
        props.$tileScaleMultiplier}px;
    }
    .expected .value {
      font-size: ${props =>
        12 *
        (props.$isLargeTiles ? (props.$isLongExpectedText ? 3 : 4) : 1) *
        props.$tileScaleMultiplier}px;
    }
  }
`;
